/**
 * A React-component that represents a save button that when pressed flashes
 * to a color to indicate that it has been pressed.
 *
 * To use this component you need to pass two properties:
 *
 *
 * - onSave
 * - disabled
 *
 * Where onSave is a function that is called when the button is pressed and
 * disabled controls if the button should be shown disabled or not.
 *
 * This component uses a separate CSS-file (SaveButton.css) that defines the
 * styling and animations.
 *
 * @example
 *
 * import SaveButton from '~/components/form/SaveButton';
 *
 */

import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import "./SaveButton.css";

/**==============================================================================
 * Constants
 */

/**
 * Enum with the different states the main component can have.
 *
 * @property {Symbol} START - Starting state, waiting for user input
 * @property {Symbol} CLICKED - When the button has been clicked
 */
enum States {
  START,
  CLICKED,
}

/**==============================================================================
 */

const FLASH_DURATION = 2;

const SaveButton: FC<{
  onSave: () => void;
  disabled?: boolean;
  children: ReactNode;
}> = ({ onSave, disabled, children }) => {
  const [currentState, setCurrentState] = useState(States.START);

  const flashTimeoutRef = useRef<NodeJS.Timeout | undefined>();
  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    setCurrentState(States.CLICKED);

    if (flashTimeoutRef.current !== undefined) {
      clearTimeout(flashTimeoutRef.current);
    }

    flashTimeoutRef.current = setTimeout(() => {
      setCurrentState(States.START);
    }, 1000 * FLASH_DURATION);

    onSave();
  }, [onSave]);

  return (
    <button
      onClick={handleClick}
      className="button button-fill SaveButton"
      style={
        currentState === States.CLICKED
          ? {
              animationDuration: `${FLASH_DURATION}s`,
              animationName: "SaveButton-blink",
            }
          : undefined
      }
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export default SaveButton;
